import { Loader2Icon } from "lucide-react";

import DrawerEmptyState from "@/components/Playground/Drawer/DrawerEmptyState";
import { Button } from "@/components/ui/button";
import { EntityIcon } from "@/components/ui/icons";
import { EntityType } from "@/queries";

interface EmptyEvaluateStateProps {
  canCreateDraft: boolean;
  canEdit: boolean;
  createError?: string | null;
  isPreparingDraft: boolean;
  onCreateDraft: () => void;
  onReferenceSheet: () => void;
}

const EmptyEvaluateState = ({
  canCreateDraft,
  canEdit,
  createError,
  isPreparingDraft,
  onCreateDraft,
  onReferenceSheet,
}: EmptyEvaluateStateProps) => {
  if (!canEdit) {
    return (
      <DrawerEmptyState
        data-playground-outputs-auto-fit-content
        icon={<EntityIcon type={EntityType.SMART_TABLE} />}
        title="No evaluation connected"
        description="An editor can prepare a draft evaluation or use an existing Table."
      />
    );
  }

  if (isPreparingDraft) {
    return (
      <DrawerEmptyState
        data-playground-outputs-auto-fit-content
        data-playground-outputs-auto-fit-ready="false"
        icon={<Loader2Icon className="animate-spin" />}
        title="Preparing evaluation"
        description="Setting up a draft from this prompt and its current inputs."
        action={
          <span className="sr-only" role="status" aria-live="polite">
            Creating evaluation draft.
          </span>
        }
      />
    );
  }

  const description =
    createError ||
    (canCreateDraft
      ? "Create a draft evaluation, or use an existing Table."
      : "Use an existing Table to evaluate this prompt.");

  return (
    <DrawerEmptyState
      data-playground-outputs-auto-fit-content
      icon={<EntityIcon type={EntityType.SMART_TABLE} />}
      title={createError ? "Evaluation couldn’t be prepared" : "No evaluation source"}
      description={description}
      action={
        <div className="flex items-center gap-1.5">
          {canCreateDraft && (
            <Button type="button" size="sm" onClick={onCreateDraft}>
              Create Draft Evaluation
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            variant={canCreateDraft ? "ghost" : "outline"}
            onClick={onReferenceSheet}
            className="text-gray-600 shadow-none hover:text-gray-900"
          >
            Use Existing Table
          </Button>
        </div>
      }
    />
  );
};

export default EmptyEvaluateState;
